import { toast } from "sonner";
import { URLMetadata } from "../types";

// Extract YouTube video ID from various YouTube URL formats
function extractYouTubeVideoId(url: string): string | null {
  const pattern = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/e\/|youtube\.com\/user\/[^\/]+\/[^\/]+\/|youtube\.com\/[^\/]+\/[^\/]+\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/i;
  const match = url.match(pattern);
  return match ? match[1] : null;
}

async function fetchYouTubeMetadata(videoId: string): Promise<URLMetadata> {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
    const data = await response.json();

    return {
      title: data.title || 'YouTube Video',
      description: `YouTube video by ${data.author_name || 'Unknown'}`,
      image: data.thumbnail_url ? data.thumbnail_url.replace('hqdefault', 'maxresdefault') : undefined,
      siteName: 'YouTube',
      favicon: 'https://www.youtube.com/favicon.ico'
    };
  } catch (error) {
    console.error('Error fetching YouTube metadata:', error);
    return {
      title: 'YouTube Video',
      description: 'No description available',
      image: undefined,
      siteName: 'YouTube',
      favicon: 'https://www.youtube.com/favicon.ico'
    };
  }
}

export async function extractURLMetadata(url: string): Promise<URLMetadata> {
  // Create an AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    // Create a safe fallback
    const fallback: URLMetadata = {
      title: url,
      description: 'No description available',
      image: undefined,
      siteName: undefined,
      favicon: undefined
    };

    // Check if it's a YouTube URL
    const youtubeVideoId = extractYouTubeVideoId(url);
    if (youtubeVideoId) {
      clearTimeout(timeoutId);
      return await fetchYouTubeMetadata(youtubeVideoId);
    }

    // Handle news sites that might block regular scraping
    const newsPatterns = {
      'cnbc.com': /cnbc\.com\/\d{4}\/\d{2}\/\d{2}\/(.*?)\.html/,
      'reuters.com': /reuters\.com\/.*?\/.*?-id/,
      'bloomberg.com': /bloomberg\.com\/news\/articles\//
    };

    for (const [domain, pattern] of Object.entries(newsPatterns)) {
      if (url.includes(domain)) {
        // Try to extract title from URL for news sites
        const match = url.match(pattern);
        if (match) {
          clearTimeout(timeoutId);
          const urlTitle = match[1]
            .replace(/-/g, ' ')
            .replace(/(\d{4})\/(\d{2})\/(\d{2})/, '')
            .trim();
          
          return {
            title: urlTitle.charAt(0).toUpperCase() + urlTitle.slice(1),
            description: `Article from ${domain.replace('.com', '').toUpperCase()}`,
            image: undefined,
            siteName: domain.replace('.com', ''),
            favicon: undefined
          };
        }
      }
    }

    // For other URLs, use the existing proxy method
    const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch URL content: ${response.status}`);
    }

    // Check Content-Length header
    const contentLength = response.headers.get('Content-Length');
    if (contentLength && parseInt(contentLength) > 5 * 1024 * 1024) { // 5MB limit
      throw new Error('URL content is too large to process');
    }

    let data;
    try {
      data = await response.json();
    } catch (error) {
      return fallback;
    }
    
    if (!data.contents) {
      return fallback;
    }

    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(data.contents, 'text/html');

    // Helper function to safely get content
    const getMetaContent = (selector: string): string | undefined => {
      const element = doc.querySelector(selector);
      return element?.getAttribute('content') || undefined;
    };

    // Extract metadata with fallbacks
    const metadata: URLMetadata = {
      title: getMetaContent('meta[property="og:title"]') ||
             getMetaContent('meta[name="twitter:title"]') ||
             doc.querySelector('title')?.textContent ||
             fallback.title,

      description: getMetaContent('meta[property="og:description"]') ||
                  getMetaContent('meta[name="twitter:description"]') ||
                  getMetaContent('meta[name="description"]') ||
                  fallback.description,

      image: getMetaContent('meta[property="og:image"]') ||
             getMetaContent('meta[name="twitter:image"]'),

      siteName: getMetaContent('meta[property="og:site_name"]') ||
                new URL(url).hostname.replace('www.', ''),

      favicon: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`
    };

    // Clean up the title if it's from the URL
    if (metadata.title && (metadata.title === url || metadata.title === url.split('/').pop())) {
      metadata.title = metadata.title
        .replace(/\.[^/.]+$/, '') // Remove file extension
        .replace(/-/g, ' ') // Replace hyphens with spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize words
        .join(' ');
    }

    return metadata;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    console.error('Error extracting metadata:', error);
    
    // Handle specific error cases
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        toast.error('Metadata extraction timed out');
      } else if (error.message.includes('Failed to fetch')) {
        toast.error('Could not access the URL');
      } else {
        toast.error('Failed to extract URL metadata');
      }
    } else {
      toast.error('Failed to extract URL metadata');
    }

    // Return URL as title if everything fails
    return {
      title: url,
      description: 'No description available',
      image: undefined,
      siteName: undefined,
      favicon: undefined
    };
  }
} 